import {
  WIDGET_COMPONENT,
  ENTITY_TYPES,
  XUX_GRID_ACTIONS,
  BUTTON_FLEX_ORDER,
  IEntityFilterItem,
} from '../../types';
import { forEach, get, isNil, keys, merge } from 'lodash';
import { BTN_CANCEL, BTN_DONE } from '@text-content/button-templates';
import {
  DEFAULT_GRID_MIN_HEIGHT,
  WIDGET_FAMILY,
} from '@config/global-constants';
import { I18n } from '@app/i18n/strings-keys';
import { getProductColumns, ObjectId } from '@app/shared/utils';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, rx } from '@app/shared/rx';
import { setFormFieldValue } from '@app/components/form/services/schema-form/schema-form-functions';
import { XviCommissionRateService } from './xvi-commission-rate.service';

export interface ICommissionRateRulesModalOptions {
  commissionRateId?: string;
  excludeCommissionRateIds?: string[];
  employeeFullName?: string;
  rateName?: string;
  isNoCommissionRate?: boolean;
}

const commissionRateRulesModal = (
  commissionRateService: XviCommissionRateService,
  options: ICommissionRateRulesModalOptions = {
    commissionRateId: '',
    excludeCommissionRateIds: [],
    isNoCommissionRate: false,
  },
): any => {
  const i18nService = commissionRateService.i18nService;
  const tagsObs: BehaviorSubject<IEntityFilterItem[]> = new BehaviorSubject<
    IEntityFilterItem[]
  >([]);
  const disabledCommissionRateFieldObs: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(options?.isNoCommissionRate || false);

  const buttons = options.commissionRateId
    ? [
        {
          ...BTN_DONE,
          caption: I18n.Common.Ok,
        },
      ]
    : [
        {
          ...BTN_DONE,
          disabled: (formValue): boolean => {
            return formValue
              ? isNil(get(formValue, 'commission_rate_id')) ||
                  isNil(get(formValue, 'virtual.commission_rate_name'))
              : false;
          },
          caption: I18n.Forms.HawkerProfileForm.Assign,
        },
        BTN_CANCEL,
      ];
  const onFormReadyCB = (form: FormGroup): void => {
    setFormFieldValue(form, 'items', [], false);
    setFormFieldValue(form, 'virtual.commission_rate_name', null, false);
    commissionRateService.initCommissionRatesGridActionSubscription();
    // cancel subscriptions on form close
    commissionRateService.subscriptions.push(
      form.controls.commission_rate_id.valueChanges
        .pipe(
          rx.filter((commissionRateId: string) => {
            setFormFieldValue(form, 'items', [], false);
            setFormFieldValue(
              form,
              'virtual.commission_rate_name',
              null,
              false,
            );
            tagsObs.next([]);
            const filterModel =
              commissionRateService.listGridApi?.getFilterModel() || {};
            forEach(keys(filterModel), (key) => {
              commissionRateService.listGridApi?.destroyFilter(key);
            });

            return !isNil(commissionRateId);
          }),
          rx.mergeMap((commissionRateId: string) => {
            commissionRateService.isCommissionRatesGridBusy$.next(true);
            disabledCommissionRateFieldObs.next(true);

            return commissionRateService.fetchCommissionRateDetails(
              commissionRateId,
            );
          }),
        )
        .subscribe(({ productToTree, tags, name }) => {
          disabledCommissionRateFieldObs.next(
            options?.isNoCommissionRate || false,
          );
          setFormFieldValue(form, 'items', productToTree, false);
          setFormFieldValue(form, 'virtual.commission_rate_name', name, false);

          commissionRateService.isCollapsed = true;
          commissionRateService.isExpandAllProducts$.next(
            commissionRateService.i18nService.translate(
              I18n.Forms.HawkerProfileForm.OperateAllProducts,
              {
                action: commissionRateService.i18nService.translate(
                  I18n.Common.Expand,
                ),
              },
            ),
          );
          tagsObs.next(tags as IEntityFilterItem[]);
          commissionRateService.isCommissionRatesGridBusy$.next(false);
          setTimeout(() => {
            commissionRateService.listGridApi?.forEachNode((node) => {
              node.setExpanded(get(node, 'data.isMajorCategoryRow', false));
            });
          }, 0);
        }),
    );
    setFormFieldValue(
      form,
      'commission_rate_id',
      options.commissionRateId,
      false,
    );

    commissionRateService.isExpandAllProducts$.next(
      commissionRateService.i18nService.translate(
        I18n.Forms.HawkerProfileForm.OperateAllProducts,
        {
          action: commissionRateService.i18nService.translate(
            I18n.Common.Expand,
          ),
        },
      ),
    );
  };

  let title = i18nService.translate(
    options.commissionRateId
      ? I18n.Forms.HawkerProfileForm.CommissionRateRules
      : I18n.Forms.HawkerProfileForm.AssignCommissionRate,
  );
  const titleSuffix = options.commissionRateId
    ? options.rateName
    : options.employeeFullName;
  if (titleSuffix) title = `${title} - ${titleSuffix}`;

  return merge(
    {},
    {
      showLoader: true,
      title,
      subTitleObs: options.commissionRateId
        ? commissionRateService.commissionRatesDescription$
        : null,
      validation: true,
      modalClass: `modal-hg commission-rate-rules-modal
                    ${options.commissionRateId ? '' : 'without-rules'}`,
      buttons,
      value: {
        commission_rate_id: null,
        items: [],
        virtual: {
          commission_rate_name: null,
        },
      },
      schema: {
        type: 'object',
        properties: {
          commission_rate_id: {
            type: 'object',
          },
          items: {
            type: 'array',
          },
        },
      },
      formRules: {
        fields: {
          commission_rate_id_section: {
            widget: WIDGET_COMPONENT.panel_group,
            title: I18n.Forms.HawkerProfileForm.AssignCommissionRate,
          },
          commission_rate_item_section: {
            widget: WIDGET_COMPONENT.panel_group,
            title: I18n.Forms.HawkerProfileForm.CommissionRateRules,
          },
          commission_rate_id: {
            title: I18n.EntityNames.CommissionRate,
            widget: WIDGET_COMPONENT.entity_select,
            widgetFamily: WIDGET_FAMILY.XUX,
            placeholder: options?.isNoCommissionRate
              ? I18n.Forms.HawkerProfileForm.NoCommissionRate
              : '',
            required: true,
            trim: true,
            show: !options.commissionRateId,
            disabled: disabledCommissionRateFieldObs,
            widgetData: {
              entity: ENTITY_TYPES.COMMISSION_RATE,
              excludeSelectItems: (): ObjectId[] =>
                options.excludeCommissionRateIds || [],
              lazy: true,
              valueField: '_id',
              dataSourceOptions: {
                defaultQuery: {
                  orderby: 'name asc',
                  top: 10000,
                },
              },
            },
          },
          items: {
            title: options.commissionRateId
              ? null
              : I18n.Forms.HawkerProfileForm.CommissionRateRules,
            ignoreAutoTitleWhenTranslate: true,
            descriptionObs: options.commissionRateId
              ? null
              : commissionRateService.commissionRatesDescription$,
            action: commissionRateService.commissionRatesGridSubject,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.edit_grid_custom,
            class: 'one-column-tree-grid',
            widgetData: {
              showLoader: commissionRateService.isCommissionRatesGridBusy$,
              showSearch: true,
              canAdd: false,
              canEdit: false,
              orderControls: false,
              canRemove: false,
              disableColumnSelector: true,
              multiselect: false,
              treeViewMode: true,
              defaultExpanded: false,
              gridMinHeight: DEFAULT_GRID_MIN_HEIGHT,
              treeChildrenPropertyName: 'children',
              userGridOptions: {
                deltaRowDataMode: false,
                suppressMovableColumns: true,
                suppressHorizontalScroll: false,
                suppressClickEdit: true,
                suppressRowClickSelection: true,
                suppressRowTransform: true,
                rowClassRules: {
                  'major-category-row': (params): boolean =>
                    get(params, 'data.isMajorCategoryRow', false),
                  'minor-category-row': (params): boolean =>
                    get(params, 'data.isMinorCategoryRow', false),
                  'leaf-node-row': (params): boolean =>
                    get(params, 'data.isLeaf', false),
                },
                overlayNoRowsTemplate: i18nService.instant(
                  I18n.Common.NoRowsToShow,
                ),
              },
              columns: [
                ...getProductColumns({ tagFilterValues: tagsObs }),
                {
                  title: I18n.Forms.HawkerProfileForm.Rate,
                  path: 'rate',
                  defaultView: true,
                  sortable: false,
                  valueFormatter: ({ value }): string =>
                    isNil(value) ? '' : `${value}%`,
                  filterParams: {
                    quickFilter: true,
                  },
                },
              ],
              buttons: [
                {
                  captionObs: commissionRateService.isExpandAllProducts$,
                  value: 'expandAll',
                  flexOrder: BUTTON_FLEX_ORDER.LEFT,
                  click: commissionRateService.expandAllProductsClick.bind(
                    commissionRateService,
                  ),
                },
              ],
              eventHandlers: {
                [XUX_GRID_ACTIONS.GRID_READY]:
                  commissionRateService.listModalGridAction.bind(
                    commissionRateService,
                  ),
              },
            },
          },
        },
        virtual: {
          type: 'object',
          properties: {
            commission_rate_name: {
              type: 'string',
            },
          },
        },
      },
      onFormReady: onFormReadyCB,
      onFormClose: () => {
        commissionRateService.subscriptions.forEach((subscription) =>
          subscription.unsubscribe(),
        );
      },
    },
  );
};

export const commissionRateRulesModalDefinition = {
  commissionRateRulesModal,
};
